import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { IMAGES, METRICS } from "../../data";

const line1 = "Offrez à vos équipes";
const line2 = "une parenthèse bien-être";
const line3 = "qui soude, motive et se";
const line4 = "prolonge 21 jours";
const LINES = [line1, line2, line3, line4];

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative isolate min-h-[100svh] w-full overflow-hidden bg-ocean-dark" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale, willChange: "transform" }}>
        <img
          src={IMAGES.hero}
          alt="Hôtel 4 étoiles aux Antilles"
          className="w-full h-full object-cover"
          style={{ transform: "translateZ(0)", backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ocean-dark/70 via-ocean-dark/45 to-ocean-dark/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-ocean-dark/70 to-transparent" />
      <div className="absolute inset-0 grain-overlay opacity-[0.12] pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-6 sm:px-10 lg:px-16 pt-32 sm:pt-40 pb-16 min-h-[100svh] flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7 }}
          className="flex items-center gap-2 mb-6"
        >
          <span className="flex items-center gap-1.5 rounded-full border border-gold/40 bg-white/10 backdrop-blur-md px-4 py-1.5 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-gold">
            <Star className="w-3.5 h-3.5 fill-gold text-gold" />
            Offre exclusive CSE & CE • Antilles 4 étoiles
          </span>
        </motion.div>

        <h1 className="font-serif font-bold text-white tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-[4.4rem] max-w-4xl">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.5 + i * 0.13, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 3 ? (
                  <span className="italic text-gold">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed"
        >
          Une immersion conviviale haut de gamme en Guadeloupe & Saint-Martin.
          Challenge sportif, nutrition sur-mesure et lâcher-prise absolu, avec un ancrage
          d'habitudes saines pendant 3 semaines.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4"
        >
          <button
            onClick={() => scrollTo("#formulaire-devis")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-coral hover:bg-coral-hover text-white font-semibold px-7 py-4 text-base transition-all hover:shadow-[0_14px_40px_rgba(230,92,0,0.5)] hover:-translate-y-0.5"
            data-testid="hero-primary-cta"
          >
            Demander une proposition
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollTo("#hotels-tarifs")}
            className="inline-flex items-center justify-center rounded-full border border-white/40 bg-white/5 backdrop-blur-md text-white font-medium px-7 py-4 text-base transition-all hover:bg-white/15"
            data-testid="hero-secondary-cta"
          >
            Découvrir les 4 hôtels
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-md max-w-3xl"
        >
          {METRICS.map((m, i) => (
            <div key={i} className="px-5 py-4 bg-white/[0.03]">
              <div className="font-serif text-2xl sm:text-3xl font-semibold text-gold">{m.value}</div>
              <div className="text-[11px] sm:text-xs text-white/70 mt-1 leading-snug">{m.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
